import {
  type MarketChartPresentationBounds,
  materializeMarketChartCandles,
  materializeMarketChartLine,
} from '@morfi/backend-sdk-v2/chart-presentation';

export interface CandleContractViolation {
  readonly count: number;
  readonly fingerprint: string;
  readonly reasons: readonly string[];
}

export interface SparseChartEntry {
  readonly line: ReadonlyArray<{
    time: number;
    value: number;
    provenance?: 'authoritative' | 'left-edge' | 'closed-gap';
  }>;
  readonly lineTimeKind?: 'sample';
  readonly ohlcv?: ReadonlyArray<CandlePoint>;
  readonly virtualQuote?: string;
  readonly leftEdgeValue?: number;
  readonly bucketSeconds?: number;
  readonly candleContractViolation?: CandleContractViolation;
}

export interface CandlePoint {
  readonly time: number;
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
  readonly provenance?: 'authoritative' | 'closed-gap';
}

export function partitionCandlesByTime(
  candles: readonly CandlePoint[],
  candleWidthSeconds: number,
  nowSeconds: number,
): { readonly closed: readonly CandlePoint[]; readonly live?: CandlePoint } {
  if (!Number.isFinite(candleWidthSeconds) || candleWidthSeconds <= 0) {
    return { closed: candles };
  }
  const closed = candles.filter((candle) => candle.time + candleWidthSeconds <= nowSeconds);
  const live = candles.findLast(
    (candle) => candle.time <= nowSeconds && candle.time + candleWidthSeconds > nowSeconds,
  );
  return { closed, ...(live !== undefined ? { live } : {}) };
}

interface ChartRow {
  readonly bucket: string;
  readonly sampleTs?: string;
  readonly open?: string;
  readonly high?: string;
  readonly low?: string;
  readonly close?: string;
  readonly value?: string;
}

interface SparseChartMetadata {
  readonly virtualQuote?: string;
  readonly leftEdgeValue?: number;
  readonly bucketSeconds?: number;
  readonly requestedFrom?: string;
  readonly requestedTo?: string;
  readonly closedBefore?: string;
}

interface ParsedCandles {
  readonly candles: readonly CandlePoint[];
  readonly violation?: CandleContractViolation;
}

export function mapMarketChartRows(
  rows: readonly ChartRow[],
  chartType: 'line' | 'ohlcv',
  convert: (value: string) => number,
  metadata: SparseChartMetadata = {},
): SparseChartEntry {
  const responseMetadata = {
    ...(metadata.virtualQuote !== undefined ? { virtualQuote: metadata.virtualQuote } : {}),
    ...(metadata.leftEdgeValue !== undefined ? { leftEdgeValue: metadata.leftEdgeValue } : {}),
    ...(metadata.bucketSeconds !== undefined ? { bucketSeconds: metadata.bucketSeconds } : {}),
  };

  if (chartType === 'ohlcv') {
    const parsed = parseCandles(rows, convert, metadata.bucketSeconds);
    const bounds = presentationBounds(metadata);
    const ohlcv =
      bounds === undefined
        ? parsed.candles
        : materializeMarketChartCandles(
            parsed.candles.map((candle) => ({
              bucket: new Date(candle.time * 1000).toISOString(),
              open: String(candle.open),
              high: String(candle.high),
              low: String(candle.low),
              close: String(candle.close),
            })),
            bounds,
          ).candles.map((candle) => ({
            time: bucketToUnix(candle.bucket) ?? 0,
            open: Number(candle.open),
            high: Number(candle.high),
            low: Number(candle.low),
            close: Number(candle.close),
            provenance: candle.provenance,
          }));
    return {
      line: ohlcv.map((candle) => ({
        time: candle.time,
        value: candle.close,
        ...(candle.provenance === undefined ? {} : { provenance: candle.provenance }),
      })),
      ohlcv,
      ...responseMetadata,
      ...(parsed.violation !== undefined ? { candleContractViolation: parsed.violation } : {}),
    };
  }

  const byTime = new Map<number, { time: number; value: number }>();
  for (const row of rows) {
    const time = bucketToUnix(row.sampleTs ?? row.bucket);
    if (time === undefined || row.value === undefined) continue;
    const value = convert(row.value);
    if (!Number.isFinite(value)) continue;
    byTime.set(time, { time, value });
  }
  const sparseLine = [...byTime.values()].toSorted((left, right) => left.time - right.time);
  const bounds = rows.some((row) => row.sampleTs !== undefined)
    ? undefined
    : presentationBounds(metadata);
  const line =
    bounds === undefined
      ? sparseLine
      : materializeMarketChartLine(
          sparseLine.map((point) => ({
            bucket: new Date(point.time * 1000).toISOString(),
            value: String(point.value),
          })),
          bounds,
        ).map((point) => ({
          time: bucketToUnix(point.displayAt) ?? 0,
          value: Number(point.value),
          provenance: point.provenance,
        }));
  return {
    line,
    ...(rows.some((row) => row.sampleTs !== undefined) ? { lineTimeKind: 'sample' as const } : {}),
    ...responseMetadata,
  };
}

function parseCandles(
  rows: readonly ChartRow[],
  convert: (value: string) => number,
  bucketSeconds: number | undefined,
): ParsedCandles {
  const byTime = new Map<number, CandlePoint>();
  const reasons: string[] = [];

  for (const row of rows) {
    const time = bucketToUnix(row.bucket);
    if (time === undefined) {
      reasons.push('invalid_bucket');
      continue;
    }
    if (
      row.open === undefined ||
      row.high === undefined ||
      row.low === undefined ||
      row.close === undefined
    ) {
      reasons.push('missing_ohlc');
      continue;
    }
    const candle = {
      time,
      open: convert(row.open),
      high: convert(row.high),
      low: convert(row.low),
      close: convert(row.close),
    };
    if (!Object.values(candle).every(Number.isFinite)) {
      reasons.push('non_finite_ohlc');
      continue;
    }
    if (
      candle.low > Math.min(candle.open, candle.close) ||
      candle.high < Math.max(candle.open, candle.close) ||
      candle.low > candle.high
    ) {
      reasons.push('invalid_ohlc_range');
      continue;
    }
    byTime.set(time, candle);
  }

  const candles = [...byTime.values()].toSorted((left, right) => left.time - right.time);
  if (validBucketSeconds(bucketSeconds)) {
    for (let index = 1; index < candles.length; index++) {
      const previous = candles[index - 1];
      const current = candles[index];
      if (
        previous !== undefined &&
        current !== undefined &&
        (current.time - previous.time) % bucketSeconds !== 0
      ) {
        reasons.push('misaligned_bucket');
      }
    }
  }

  if (reasons.length === 0) return { candles };
  const sortedReasons = [...new Set(reasons)].toSorted();
  return {
    candles,
    violation: {
      count: reasons.length,
      reasons: sortedReasons,
      fingerprint: `${reasons.length}|${sortedReasons.join(',')}`,
    },
  };
}

function presentationBounds(
  metadata: SparseChartMetadata,
): MarketChartPresentationBounds | undefined {
  if (
    !validBucketSeconds(metadata.bucketSeconds) ||
    metadata.requestedFrom === undefined ||
    metadata.requestedTo === undefined ||
    metadata.closedBefore === undefined
  ) {
    return undefined;
  }
  return {
    rangeStart: metadata.requestedFrom,
    rangeEnd: metadata.requestedTo,
    bucketSeconds: metadata.bucketSeconds,
    closedBefore: metadata.closedBefore,
    gapPolicy: 'fill-closed',
    ...(metadata.leftEdgeValue === undefined
      ? {}
      : { leftEdgeValue: String(metadata.leftEdgeValue) }),
  };
}

function validBucketSeconds(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value > 0;
}

function bucketToUnix(bucket: string): number | undefined {
  const ms = Date.parse(bucket);
  return Number.isFinite(ms) ? ms / 1000 : undefined;
}
